import { createAsyncThunk, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { RootState } from '.';

import { isEqual } from 'lodash';
import { getRepairOrderByRepairOrderNumber } from '../api/dms/repairOrder.api';
import { clockOffAttendance, clockOffLabour, clockOnAttendance, clockOnLabour } from '../api/dms/sync.api';
import { getTechClocking } from '../api/dms/technician.api';
import { logConsole, logTransaction } from '../common/log';
import { getStateDateAsString, getStateStringAsDate, handleThunkError } from '../common/util';
import { AttachmentAssetType, getEmptyAttachmentContainer } from '../models/dms/attachment.model';
import { ClockingDirection, ClockingType, IAttendanceClocking, ILabourClocking, ILabourClockingDetails, ITechClockingDetail } from '../models/dms/clocking.model';
import { IRepairOrder } from '../models/dms/repairOrder.model';
import { IRepairOrderJob } from '../models/dms/repairOrderJob.model';
import { IClockingRequestQueueItem, RequestQueueGroup, RequestQueueType } from '../models/dms/requestQueue.model';
import { ADHOC_REG, IWorkshopVehicleDetails } from '../models/dms/workshopVehicle.model';
import { IThunkError } from '../models/error.model';
import { IGeoposition } from '../models/geolocation.model';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { addClockingFetchCooldownState, setIsLoadingCountState } from './appSlice';
import { resetDistanceTrackingDataStoreAsync } from './locationSlice';
import { cleanRepairOrderSyncStoreAsync } from './repairOrderSyncSlice';
import { addToRequestQueueStoreAsync } from './requestQueueSlice';
import { setAdhocCommentCacheStoreAsync } from './technicianCommentSlice';
import { setAdhocAttachmentCacheStoreAsync, setSelectedVehicleAttachmentsStoreAsync } from './vehicleAttachmentSlice';
import { fetchWorkshopVehicleDetailsByRegistrationNumberStoreAsync, setSelectedWorkshopVehicleStoreAsync } from './workshopVehicleSlice';
import { setSelectedStockUnitStoreAsync } from './stockUnitSlice';
import { setSelectedFixedAssetStoreAsync } from './fixedAssetSlice';
import { IStockUnitDetails } from '../models/dms/stockUnit.model';
import { IFixedAssetDetails } from '../models/dms/fixedAsset.model';

interface ITechnicianClockingState {
  attendanceClocking?: IAttendanceClocking;
  labourClocking?: ILabourClocking;
  serverClockingDetail?: ITechClockingDetail;
  serverClockingDetailLoading: boolean;
}

const initialState: ITechnicianClockingState = {
  attendanceClocking: undefined,
  labourClocking: undefined,
  serverClockingDetail: undefined,
  serverClockingDetailLoading: false,
};

/// THUNKS

export const setAttendanceClockingStoreAsync = createAsyncThunk<{}, IAttendanceClocking | undefined>('technicianClocking/setAttendanceClockingStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting attendance clocking to ${val?.direction}`, '[technicianClocking] setAttendanceClockingStoreAsync');
  try {
    dispatch(setAttendanceClockingState(val));
    await updateConfigObject<IAttendanceClocking>(ConfigName.AttendanceClocking, val || ({} as IAttendanceClocking), false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setLabourClockingStoreAsync = createAsyncThunk<{}, ILabourClocking | undefined>('technicianClocking/setLabourClockingStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting labour clocking to ${val?.direction} ${val?.jobData ? val?.jobData.repairOrderNumber + '/' + val?.jobData.jobCode : ''}`, '[technicianClocking] setLabourClockingStoreAsync');
  try {
    dispatch(setLabourClockingState(val));
    await updateConfigObject<ILabourClocking>(ConfigName.LabourClocking, val || ({} as ILabourClocking), false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setServerClockingDetailStoreAsync = createAsyncThunk<{}, ITechClockingDetail | undefined>('technicianClocking/setServerClockingStoreAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting clocking from server`, '[technicianClocking] setServerClockingStoreAsync');
  try {
    // This function will be called often. To be more efficient, only store the
    // value if the value changes
    const newServerClockingsString = val;
    const currServerClockingsString = (getState() as RootState).technicianClocking.serverClockingDetail || ({} as ITechClockingDetail);

    if (val) {
      if (!isEqual(newServerClockingsString, currServerClockingsString)) {
        logTransaction(LogType.Info, `Server clocking different from local clocking`, '[technicianClocking] setServerClockingStoreAsync');
        logTransaction(LogType.Info, `Attendance - startDateTime: ${val.attendanceClocking.startDateTime}, endDateTime: ${val.attendanceClocking.endDateTime}`, '[technicianClocking] setServerClockingStoreAsync');
        logTransaction(
          LogType.Info,
          `Labour - startDateTime: ${val.labourClocking.startDateTime}, endDateTime: ${val.labourClocking.endDateTime}, jobData: ${val.labourClocking.repairOrderNumber}/${val.labourClocking.jobCode}`,
          '[technicianClocking] setServerClockingStoreAsync',
        );
        dispatch(setServerClockingDetailState(val));
        await updateConfigObject<ITechClockingDetail>(ConfigName.ServerClockingDetail, val, false);
      } else {
        logTransaction(LogType.Info, `Server clocking not changed`);
      }
    } else {
      dispatch(setServerClockingDetailState(val));
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchClockingDetailsAsync = createAsyncThunk<IThunkError | ITechClockingDetail | undefined, string>('technicianClocking/fetchClockingDetailsAsync', async (techCode, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Fetching clocking details from server`, '[technicianClocking] fetchClockingDetailsAsync');
  try {
    //dispatch(setIsLoadingDataState(true));
    dispatch(setServerClockingDetailLoadingState(true));

    const responseData = await getTechClocking(techCode);
    await dispatch(setServerClockingDetailStoreAsync(responseData));

    //dispatch(setIsLoadingDataState(false));
    dispatch(setServerClockingDetailLoadingState(false));

    return responseData;
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setServerClockingDetailLoadingState(false));
  }
});

export const clockOnAttendanceClockingAsync = createAsyncThunk<{}, { techCode: string; startDateTime: string; position?: IGeoposition }>(
  'technicianClocking/clockOnAttendanceClockingAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Clocking IN attendance - startDateTime: ${payload.startDateTime}`, '[technicianClocking] clockOnAttendanceClockingAsync');
    try {
      dispatch(addClockingFetchCooldownState());
      const currentAttendance = (getState() as RootState).technicianClocking.attendanceClocking;

      // Ensure attendance is currently not already on
      if (currentAttendance?.direction === ClockingDirection.On) {
        const err = 'Cannot clock IN attendance, attendance is already clock IN';
        logTransaction(LogType.Error, err);
        return handleThunkError(err, rejectWithValue);
      }

      await dispatch(
        setAttendanceClockingStoreAsync({
          direction: ClockingDirection.On,
          type: ClockingType.Attendance,
          technicianCode: payload.techCode,
          startDateTime: payload.startDateTime,
          fromServer: false,
        } as IAttendanceClocking),
      );

      await dispatch(
        addToRequestQueueStoreAsync({
          id: new Date().getTime(),
          group: RequestQueueGroup.Clocking,
          type: RequestQueueType.TechAttendanceOn,
          immediate: true,
          data: {
            technicianCode: payload.techCode,
            startDateTime: payload.startDateTime,
            position: payload.position,
            type: ClockingType.Attendance,
            direction: ClockingDirection.On,
          } as IAttendanceClocking,
        } as IClockingRequestQueueItem),
      );
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const clockOffAttendanceClockingAsync = createAsyncThunk<{}, { techCode: string; endDateTime: string; position?: IGeoposition }>(
  'technicianClocking/clockOffAttendanceClockingAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Clocking OUT attendance - endDateTime: ${payload.endDateTime}`, '[technicianClocking] clockOffAttendanceClockingAsync');
    try {
      dispatch(addClockingFetchCooldownState());
      const currentLabour = (getState() as RootState).technicianClocking.labourClocking;
      const currentAttendance = (getState() as RootState).technicianClocking.attendanceClocking;

      // Ensure labour is currently not already on
      if (currentLabour?.direction === ClockingDirection.On) {
        const err = 'Cannot clock OUT attendance, technician is currently clocked ON labour';
        logTransaction(LogType.Error, err);
        return handleThunkError(err, rejectWithValue);
      }

      // Ensure attendance is currently not already off
      if ((currentAttendance?.direction ?? ClockingDirection.Off) === ClockingDirection.Off) {
        const err = 'Cannot clock OUT attendance, attendance is already clocked OUT';
        logTransaction(LogType.Error, err);
        return handleThunkError(err, rejectWithValue);
      }

      await dispatch(
        setAttendanceClockingStoreAsync({
          direction: ClockingDirection.Off,
          type: ClockingType.Attendance,
          technicianCode: payload.techCode,
          startDateTime: currentAttendance?.startDateTime,
          endDateTime: payload.endDateTime,
          fromServer: false,
        } as IAttendanceClocking),
      );

      if (currentLabour?.direction === ClockingDirection.Break) {
        // If labour on break, set local state to clocked off
        const msg = `Labour was on BREAK, setting to OFF`;
        logTransaction(LogType.Info, msg);
        await dispatch(
          setLabourClockingStoreAsync({
            ...currentLabour,
            direction: ClockingDirection.Off,
            fromServer: false,
          }),
        );
      }

      if (currentAttendance?.direction === ClockingDirection.On) {
        // Only clock off on the server if the user is currently clocked on (not on break)
        const msg = `Attendance was on ON, setting to OFF`;
        logTransaction(LogType.Info, msg);
        await dispatch(
          addToRequestQueueStoreAsync({
            id: new Date().getTime(),
            group: RequestQueueGroup.Clocking,
            type: RequestQueueType.TechAttendanceOff,
            immediate: true,
            data: {
              technicianCode: payload.techCode,
              endDateTime: payload.endDateTime,
              position: payload.position,
              type: ClockingType.Attendance,
              direction: ClockingDirection.Off,
            } as IAttendanceClocking,
          } as IClockingRequestQueueItem),
        );
      }
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const breakAttendanceClockingAsync = createAsyncThunk<{}, { techCode: string; endDateTime: string; position?: IGeoposition }>('technicianClocking/breakAttendanceClockingAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Clocking BREAK attendance - endDateTime: ${payload.endDateTime}`, '[technicianClocking] breakAttendanceClockingAsync');
  try {
    dispatch(addClockingFetchCooldownState());
    const currentAttendance = (getState() as RootState).technicianClocking.attendanceClocking;

    // Perform labour clocking first
    await dispatch(breakLabourClockingAsync({ techCode: payload.techCode, endDateTime: payload.endDateTime, position: payload.position }));

    // Ensure attendance is currently not already break
    if (currentAttendance?.direction === ClockingDirection.Break) {
      const err = 'Cannot BREAK attendance, attendance is already on BREAK';
      logTransaction(LogType.Error, err);
      return handleThunkError(err, rejectWithValue);
    }

    // You can't go from off to a break
    if (currentAttendance?.direction === undefined || currentAttendance?.direction === ClockingDirection.Off) {
      const err = 'Cannot BREAK attendance, attendance is currently OFF';
      logTransaction(LogType.Error, err);
      return handleThunkError(err, rejectWithValue);
    }

    if (currentAttendance) {
      await dispatch(
        setAttendanceClockingStoreAsync({
          direction: ClockingDirection.Break,
          type: ClockingType.Attendance,
          technicianCode: payload.techCode,
          startDateTime: currentAttendance.startDateTime,
          endDateTime: payload.endDateTime,
          resumeDateTime: currentAttendance.resumeDateTime,
          fromServer: false,
        } as IAttendanceClocking),
      );
    }

    await dispatch(
      addToRequestQueueStoreAsync({
        id: new Date().getTime(),
        group: RequestQueueGroup.Clocking,
        type: RequestQueueType.TechAttendanceOff,
        immediate: true,
        data: {
          technicianCode: payload.techCode,
          endDateTime: payload.endDateTime,
          position: payload.position,
          type: ClockingType.Attendance,
          direction: ClockingDirection.Off,
        } as IAttendanceClocking,
      } as IClockingRequestQueueItem),
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const resumeAttendanceClockingAsync = createAsyncThunk<{}, { startDateTime: string; position?: IGeoposition; resumeLabour: boolean }>(
  'technicianClocking/resumeAttendanceClockingAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Clocking RESUME attendance - startDateTime: ${payload.startDateTime}`, '[technicianClocking] resumeAttendanceClockingAsync');
    try {
      dispatch(addClockingFetchCooldownState());
      const currentAttendance = (getState() as RootState).technicianClocking.attendanceClocking;

      // There must be an existing attendance clocking currently in break in memory to resume
      if (currentAttendance?.direction !== ClockingDirection.Break) {
        const err = `Cannot RESUME attendance, attendance currently not on BREAK`;
        logTransaction(LogType.Error, err);
        return handleThunkError(err, rejectWithValue);
      }

      // Since we want to keep the device displaying the "total time" clocked on minus any breaks,
      // we must add the previous clocking time to the current clocking time.
      // For exmple, a clocking from 9->10=60m, then a break from 10->11=60m, then resume at 11->11.15=15m, that is
      // 60m+15m. Total time to display is 75 minutes or 1:15.
      // This continues to calculate correctly for additional break/resume/break/resume/etc.
      const startDateTime = getStateStringAsDate(currentAttendance?.startDateTime);
      const endDateTime = getStateStringAsDate(currentAttendance?.endDateTime);
      const timePassed = +endDateTime - +startDateTime;
      const newTime = +getStateStringAsDate(payload.startDateTime) - timePassed;

      // Make the clocking on the device appear as if it was clocked on
      await dispatch(
        setAttendanceClockingStoreAsync({
          direction: ClockingDirection.On,
          type: ClockingType.Attendance,
          technicianCode: currentAttendance.technicianCode,
          startDateTime: getStateDateAsString(new Date(newTime)),
          resumeDateTime: payload.startDateTime,
          fromServer: false,
        } as IAttendanceClocking),
      );

      await dispatch(
        addToRequestQueueStoreAsync({
          id: new Date().getTime(),
          group: RequestQueueGroup.Clocking,
          type: RequestQueueType.TechAttendanceOn,
          immediate: true,
          data: {
            technicianCode: currentAttendance.technicianCode,
            startDateTime: payload.startDateTime,
            position: payload.position,
            type: ClockingType.Attendance,
            direction: ClockingDirection.On,
          } as IAttendanceClocking,
        } as IClockingRequestQueueItem),
      );
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }

    const labourClocking = (getState() as RootState).technicianClocking.labourClocking;
    if (payload.resumeLabour && labourClocking?.direction === ClockingDirection.Break) {
      try {
        await dispatch(
          resumeLabourClockingAsync({
            startDateTime: payload.startDateTime,
            position: payload.position,
          }),
        );
      } catch (err: any) {
        return handleThunkError(err, rejectWithValue);
      }
    }
  },
);

export const clockOnLabourClockingAsync = createAsyncThunk<{}, { techCode: string; startDateTime: string; jobData: IRepairOrderJob; repairOrder: IRepairOrder; position?: IGeoposition; registrationNumber?: string; retainVehicle?: boolean }>(
  'technicianClocking/clockOnLabourClockingAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Clocking ON labour - startDateTime: ${payload.startDateTime}, jobData: ${payload.jobData.repairOrderNumber}/${payload.jobData.jobCode}`, '[technicianClocking] clockOnLabourClockingAsync');
    try {
      dispatch(addClockingFetchCooldownState());
      const { attendanceClocking } = (getState() as RootState).technicianClocking;
      const currentLabour = (getState() as RootState).technicianClocking.labourClocking;

      // Ensure attendance is currently not already on
      if (currentLabour?.direction === ClockingDirection.On) {
        const err = `Cannot clock ON labour, labour is already clock ON`;
        logTransaction(LogType.Error, err);
        return handleThunkError(err, rejectWithValue);
      }

      // Ensure user is clocked in attendance
      switch (attendanceClocking?.direction) {
        case ClockingDirection.On:
          // Do nothing
          break;
        case ClockingDirection.Break:
          await dispatch(resumeAttendanceClockingAsync({ position: payload.position, startDateTime: payload.startDateTime, resumeLabour: false }));
          break;
        default:
          await dispatch(clockOnAttendanceClockingAsync({ techCode: payload.techCode, position: payload.position, startDateTime: payload.startDateTime }));
          break;
      }

      await dispatch(
        setLabourClockingStoreAsync({
          direction: ClockingDirection.On,
          type: ClockingType.Labour,
          technicianCode: payload.techCode,
          startDateTime: payload.startDateTime,
          jobData: payload.jobData,
          repairOrder: payload.repairOrder,
          fromServer: false,
        } as ILabourClocking),
      );

      await dispatch(
        addToRequestQueueStoreAsync({
          id: new Date().getTime(),
          group: RequestQueueGroup.Clocking,
          type: RequestQueueType.TechLabourOn,
          immediate: true,
          data: {
            technicianCode: payload.techCode,
            startDateTime: payload.startDateTime,
            position: payload.position,
            type: ClockingType.Labour,
            direction: ClockingDirection.On,
            jobData: payload.jobData,
            repairOrder: payload.repairOrder,
          } as ILabourClocking,
        } as IClockingRequestQueueItem),
      );

      // Clear selected attachments
      logTransaction(LogType.Info, `Clearing selected and adhoc vehicle attachments`);
      const vehicleAttachments = (getState() as RootState).vehicleAttachments.selectedVehicleAttachments;
      if (vehicleAttachments?.repairOrderNumber !== payload.repairOrder.repairOrderNumber?.toString()) {
        await dispatch(setSelectedVehicleAttachmentsStoreAsync(getEmptyAttachmentContainer(AttachmentAssetType.WorkshopVehicle)));
      }
      await dispatch(setAdhocAttachmentCacheStoreAsync([]));

      // Clear selected tech comments
      logTransaction(LogType.Info, `Clearing adhoc comments`);
      await dispatch(setAdhocCommentCacheStoreAsync([]));

      // Clear distance traveled
      logTransaction(LogType.Info, `Clearing tracked distance`);
      await dispatch(resetDistanceTrackingDataStoreAsync());

      // Clear selected vehicle
      logTransaction(LogType.Info, `Clearing selected workshop vehicle`);
      payload.retainVehicle || (await dispatch(setSelectedWorkshopVehicleStoreAsync({} as IWorkshopVehicleDetails)));
      logTransaction(LogType.Info, `Clearing selected stock unit`);
      payload.retainVehicle || (await dispatch(setSelectedStockUnitStoreAsync({} as IStockUnitDetails)));
      logTransaction(LogType.Info, `Clearing selected fixed asset`);
      payload.retainVehicle || (await dispatch(setSelectedFixedAssetStoreAsync({} as IFixedAssetDetails)));

      // Clean up any custom RO search items in cache
      try {
        if (payload.repairOrder.repairOrderNumber) {
          await dispatch(cleanRepairOrderSyncStoreAsync(payload.repairOrder.repairOrderNumber));
        }
      } catch (ex) {
        /* ignore errors */
      }

      // Pre-load vehicle data
      try {
        if (payload.jobData.isAdhoc) {
          // For adhoc clocking, load a fake vehicle
          logTransaction(LogType.Info, `Pre-loading ADHOC details for rego ${ADHOC_REG}`);
          dispatch(
            setSelectedWorkshopVehicleStoreAsync({
              registrationNumber: ADHOC_REG,
            } as IWorkshopVehicleDetails),
          );
        } else if (payload.repairOrder.vehicle?.source === 'WKV' && payload.registrationNumber) {
          logTransaction(LogType.Info, `Pre-loading WKV details for rego ${payload.registrationNumber}`);
          dispatch(fetchWorkshopVehicleDetailsByRegistrationNumberStoreAsync({ registrationNumber: payload.registrationNumber, repairOrderNumber: payload.repairOrder.repairOrderNumber || -1 }));
        } else {
          logTransaction(LogType.Info, `Pre-loading vehicle details not required for source ${payload.repairOrder.vehicle?.source}`);
        }
      } catch (ex) {
        /* ignore errors */
      }
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const clockOffLabourClockingAsync = createAsyncThunk<{}, { techCode: string; endDateTime: string; jobData: IRepairOrderJob; details: ILabourClockingDetails; position?: IGeoposition }>(
  'technicianClocking/clockOffLabourClockingAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Clocking OFF labour - endDateTime: ${payload.endDateTime}, jobData: ${payload.jobData.repairOrderNumber}/${payload.jobData.jobCode}`, '[technicianClocking] clockOffLabourClockingAsync');
    try {
      dispatch(addClockingFetchCooldownState());
      const currentLabour = (getState() as RootState).technicianClocking.labourClocking;

      // Ensure labour is currently already on
      if (currentLabour?.direction === ClockingDirection.Off) {
        const err = `Cannot clock OFF labour, labour is already clocked OFF`;
        logTransaction(LogType.Error, err);
        return handleThunkError(err, rejectWithValue);
      }

      let clockingDetails = { ...payload.details };
      // If this is an adhoc clocking,
      if (payload.jobData.isAdhoc) {
        // Capture any technician comments that were added
        const techCommentsCache = (getState() as RootState).technicianComments.adhocCommentsCache;
        clockingDetails = {
          ...clockingDetails,
          techComments: [...techCommentsCache],
        };

        // Capture any attachments that were added
        const attachments = (getState() as RootState).vehicleAttachments.adhocAttachmentsCache;
        clockingDetails = {
          ...clockingDetails,
          attachments: [...attachments],
        };
      }

      await dispatch(
        setLabourClockingStoreAsync({
          direction: ClockingDirection.Off,
          type: ClockingType.Labour,
          technicianCode: payload.techCode,
          endDateTime: payload.endDateTime,
          jobData: {
            ...payload.jobData,
          },
          repairOrder: {
            ...currentLabour?.repairOrder,
          },
          details: {
            ...clockingDetails,
          },
          fromServer: false,
        } as ILabourClocking),
      );

      await dispatch(
        addToRequestQueueStoreAsync({
          id: new Date().getTime(),
          group: RequestQueueGroup.Clocking,
          type: RequestQueueType.TechLabourOff,
          immediate: true,
          data: {
            technicianCode: payload.techCode,
            endDateTime: payload.endDateTime,
            position: payload.position,
            type: ClockingType.Labour,
            direction: ClockingDirection.Off,
            jobData: {
              ...payload.jobData,
            },
            repairOrder: {
              ...currentLabour?.repairOrder,
            },
            details: {
              ...clockingDetails,
            },
          } as ILabourClocking,
        } as IClockingRequestQueueItem),
      );
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const breakLabourClockingAsync = createAsyncThunk<{}, { techCode: string; endDateTime: string; position?: IGeoposition }>('technicianClocking/breakLabourClockingAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Clocking BREAK labour - endDateTime: ${payload.endDateTime}`, '[technicianClocking] breakLabourClockingAsync');
  try {
    dispatch(addClockingFetchCooldownState());
    const currentLabour = (getState() as RootState).technicianClocking.labourClocking;

    if (currentLabour) {
      if (currentLabour.direction === ClockingDirection.On) {
        // If labour on, break labour state
        await dispatch(
          setLabourClockingStoreAsync({
            direction: ClockingDirection.Break,
            type: ClockingType.Labour,
            technicianCode: currentLabour.technicianCode,
            startDateTime: currentLabour.startDateTime,
            endDateTime: payload.endDateTime,
            fromServer: false,
            jobData: { ...currentLabour.jobData },
            repairOrder: { ...currentLabour.repairOrder },
            details: { ...currentLabour.details },
          } as ILabourClocking),
        );

        // If labour is on, clock off on server
        await dispatch(
          addToRequestQueueStoreAsync({
            id: new Date().getTime(),
            group: RequestQueueGroup.Clocking,
            type: RequestQueueType.TechLabourOff,
            immediate: true,
            data: {
              technicianCode: payload.techCode,
              endDateTime: payload.endDateTime,
              position: payload.position,
              type: ClockingType.Labour,
              direction: ClockingDirection.Off,
              jobData: { ...currentLabour.jobData },
              repairOrder: { ...currentLabour.repairOrder },
              details: currentLabour.details,
            } as ILabourClocking,
          } as IClockingRequestQueueItem),
        );
      }
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const resumeLabourClockingAsync = createAsyncThunk<{}, { startDateTime: string; position?: IGeoposition }>('technicianClocking/resumeLabourClockingAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Clocking RESUME labour - startDateTime: ${payload.startDateTime}`, '[technicianClocking] resumeLabourClockingAsync');
  try {
    dispatch(addClockingFetchCooldownState());
    const currentAttendance = (getState() as RootState).technicianClocking.attendanceClocking;
    if (currentAttendance?.direction === ClockingDirection.Break) {
      await dispatch(
        resumeAttendanceClockingAsync({
          startDateTime: payload.startDateTime,
          position: payload.position,
          resumeLabour: false,
        }),
      );
    }
  } catch (err: any) {
    logTransaction(LogType.Info, `Failed to RESUME labour clocking - error: ${err}`);
    return handleThunkError(err, rejectWithValue);
  }

  try {
    const currentLabour = (getState() as RootState).technicianClocking.labourClocking;

    // There must be an existing attendance clocking currently in break in memory to resume
    if (currentLabour?.direction !== ClockingDirection.Break) {
      const err = `Cannot RESUME labour, labour currently not on BREAK`;
      logTransaction(LogType.Error, err);
      return handleThunkError(err, rejectWithValue);
    }

    // Since we want to keep the device displaying the "total time" clocked on minus any breaks,
    // we must add the previous clocking time to the current clocking time.
    // For exmple, a clocking from 9->10=60m, then a break from 10->11=60m, then resume at 11->11.15=15m, that is
    // 60m+15m. Total time to display is 75 minutes or 1:15.
    // This continues to calculate correctly for additional break/resume/break/resume/etc.
    const startDateTime = getStateStringAsDate(currentLabour?.startDateTime);
    const endDateTime = getStateStringAsDate(currentLabour?.endDateTime);
    const timePassed = +endDateTime - +startDateTime;
    const newTime = +getStateStringAsDate(payload.startDateTime) - timePassed;

    await dispatch(
      setLabourClockingStoreAsync({
        direction: ClockingDirection.On,
        type: ClockingType.Labour,
        technicianCode: currentLabour.technicianCode,
        startDateTime: getStateDateAsString(new Date(newTime)),
        resumeDateTime: currentLabour.resumeDateTime,
        jobData: currentLabour.jobData,
        repairOrder: currentLabour.repairOrder,
        fromServer: false,
      } as ILabourClocking),
    );

    await dispatch(
      addToRequestQueueStoreAsync({
        id: new Date().getTime(),
        group: RequestQueueGroup.Clocking,
        type: RequestQueueType.TechLabourOn,
        immediate: true,
        data: {
          technicianCode: currentLabour.technicianCode,
          startDateTime: payload.startDateTime,
          position: payload.position,
          type: ClockingType.Labour,
          direction: ClockingDirection.On,
          jobData: currentLabour.jobData,
          repairOrder: currentLabour.repairOrder,
        } as ILabourClocking,
      } as IClockingRequestQueueItem),
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const swapLabourClockingAsync = createAsyncThunk<
  {},
  { techCode: string; endDateTime: string; off: { jobData: IRepairOrderJob; details: ILabourClockingDetails }; on: { jobData: IRepairOrderJob; repairOrder: IRepairOrder }; position?: IGeoposition }
>('technicianClocking/swapLabourClockingAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Clocking SWAP labour - endDateTime: ${payload.endDateTime}, jobData: ${payload.on.jobData.repairOrderNumber}/${payload.on.jobData.jobCode}`, '[technicianClocking] swapLabourClockingAsync');
  try {
    // Clock off labour
    try {
      const labResultAction = await dispatch(
        clockOffLabourClockingAsync({
          techCode: payload.techCode,
          endDateTime: payload.endDateTime,
          jobData: payload.off.jobData,
          details: payload.off.details,
          position: payload.position,
        }),
      );
      unwrapResult(labResultAction);
    } catch (ex) {
      logTransaction(LogType.Error, (ex as IThunkError).message);
    }

    // Clock on labour
    try {
      const labResultAction = await dispatch(
        clockOnLabourClockingAsync({
          techCode: payload.techCode,
          startDateTime: getStateDateAsString(new Date()),
          jobData: payload.on.jobData,
          repairOrder: payload.on.repairOrder,
          position: payload.position,
          retainVehicle: payload.off.jobData.repairOrderNumber === payload.on.repairOrder.repairOrderNumber,
        }),
      );
      unwrapResult(labResultAction);
    } catch (ex) {
      logTransaction(LogType.Error, (ex as IThunkError).message);
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const postClockOnAttendanceAsync = createAsyncThunk<{}, { techCode: string; startDateTime: string; position?: IGeoposition }>('technicianClocking/postClockingOnAttendanceAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `POST clock IN attendance - startDateTime: ${payload.startDateTime}`, '[technicianClocking] postClockingOnAttendanceAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(addClockingFetchCooldownState());
    await clockOnAttendance((getState() as RootState).app.stateId, payload.techCode, getStateStringAsDate(payload.startDateTime), payload.position);
    dispatch(setIsLoadingCountState(-1));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

export const postClockOffAttendanceAsync = createAsyncThunk<{}, { techCode: string; endDateTime: string; position?: IGeoposition }>('technicianClocking/postClockOffAttendanceAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `POST clock OUT attendance - endDateTime: ${payload.endDateTime}`, '[technicianClocking] postClockOffAttendanceAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(addClockingFetchCooldownState());
    await clockOffAttendance((getState() as RootState).app.stateId, payload.techCode, getStateStringAsDate(payload.endDateTime), payload.position);
    dispatch(setIsLoadingCountState(-1));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

export const postClockOnLabourAsync = createAsyncThunk<{}, { techCode: string; startDateTime: string; jobData: IRepairOrderJob; repairOrder: IRepairOrder; position?: IGeoposition }>(
  'technicianClocking/postClockOnLabourAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `POST clock ON labour - startDateTime: ${payload.startDateTime}`, '[technicianClocking] postClockOnLabourAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(addClockingFetchCooldownState());
      await clockOnLabour((getState() as RootState).app.stateId, payload.techCode, getStateStringAsDate(payload.startDateTime), payload.jobData, payload.repairOrder, payload.position);
      dispatch(setIsLoadingCountState(-1));
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
    }
  },
);

export const postClockOffLabourAsync = createAsyncThunk<{}, { techCode: string; endDateTime: string; jobData: IRepairOrderJob; repairOrder: IRepairOrder; details?: ILabourClockingDetails; position?: IGeoposition }>(
  'technicianClocking/postClockOffLabourAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `POST clock off labour - endDateTime: ${payload.endDateTime}, jobData: ${payload.jobData.repairOrderNumber}/${payload.jobData.jobCode}`, '[technicianClocking] postClockOffLabourAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(addClockingFetchCooldownState());
      await clockOffLabour((getState() as RootState).app.stateId, payload.techCode, getStateStringAsDate(payload.endDateTime), payload.jobData, payload.repairOrder, payload.details, payload.position);
      dispatch(setIsLoadingCountState(-1));
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
    }
  },
);

export const setLabourClockingRepairOrderStoreAsync = createAsyncThunk<{}, number>('technicianClocking/setLabourClockingRepairOrderStoreAsync', async (repairOrderNumber, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting labour clocking repair order to ${repairOrderNumber}`, '[technicianClocking] setLabourClockingRepairOrderStoreAsync');
  try {
    const labourClocking = (getState() as RootState).technicianClocking.labourClocking;

    if (labourClocking) {
      const newLabourClocking = { ...labourClocking };
      const repairOrderDetails = await getRepairOrderByRepairOrderNumber(repairOrderNumber);
      const repairOrder = {
        branch: repairOrderDetails.branch.code,
        driver: repairOrderDetails.driver,
        dropOffDateTime: repairOrderDetails.dropOffDateTime,
        owner: repairOrderDetails.owner,
        pickUpDateTime: repairOrderDetails.pickUpDateTime,
        progressStatus: repairOrderDetails.progressStatus,
        registrationNumber: repairOrderDetails.registrationNumber,
        repairOrderNumber: repairOrderDetails.repairOrderNumber,
        vehicle: repairOrderDetails.vehicle,
        jobs: repairOrderDetails.jobs.map((j) => {
          return {
            branchCode: repairOrderDetails.branch.code,
            detailLine: j.description,
            jobCode: j.code,
            jobType: j.type,
            repairOrderNumber: repairOrderDetails.repairOrderNumber,
            isInvoiced: j.isInvoiced,
          } as IRepairOrderJob;
        }),
      } as IRepairOrder;

      newLabourClocking.repairOrder = { ...repairOrder };
      await dispatch(setLabourClockingStoreAsync(newLabourClocking));
      return newLabourClocking;
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setLabourClockingIsTrackingDistanceStoreAsync = createAsyncThunk<{}, boolean>('technicianClocking/setLabourClockingIsTrackingDistanceStoreAsync', async (isTrackingDistance, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting labour clocking is tracking distance to ${isTrackingDistance ? 'true' : false}`, '[technicianClocking] setLabourClockingIsTrackingDistanceStoreAsync');
  try {
    const lc = (getState() as RootState).technicianClocking.labourClocking;

    if (lc) {
      const newLabourClocking = { ...lc };

      newLabourClocking.trackDistance = isTrackingDistance;
      await dispatch(setLabourClockingStoreAsync(newLabourClocking));
      return newLabourClocking;
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

/// SLICE

export const technicianClockingSlice = createSlice({
  name: 'technicianClocking',
  initialState,
  reducers: {
    setAttendanceClockingState: (state, action: PayloadAction<IAttendanceClocking | undefined>) => {
      logConsole(LogType.Info, '[technicianClocking] setAttendanceClockingState');
      state.attendanceClocking = action.payload;
    },
    setLabourClockingState: (state, action: PayloadAction<ILabourClocking | undefined>) => {
      logConsole(LogType.Info, '[technicianClocking] setLabourClockingState');
      state.labourClocking = action.payload;
    },
    setServerClockingDetailState: (state, action: PayloadAction<ITechClockingDetail | undefined>) => {
      logConsole(LogType.Info, '[technicianClocking] setServerClockingDetailState');
      state.serverClockingDetail = action.payload;
    },
    setServerClockingDetailLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, '[technicianClocking] setServerClockingDetailLoadingState');
      state.serverClockingDetailLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setAttendanceClockingState, setLabourClockingState, setServerClockingDetailState, setServerClockingDetailLoadingState } = technicianClockingSlice.actions;

/// SELECTORS

export default technicianClockingSlice.reducer;
